import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../../apiclient/apiclient";
import socket from "../../../socket/socketClient";
import { Clock, Truck, PackageCheck, CheckCircle } from "lucide-react";
import ConfirmDialog from "../../../components/ConfirmDialog"; // Reuse your dialog for modal
import PageHeader from "../../../components/PageHeader";
import Loader from "../../../components/Loader";
/* ---------------- GREETING ---------------- */
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
};

const AgentDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
const [loading, setLoading] = useState(true);
  const userName = (user?.name || "Agent").toUpperCase();

  const [greeting, setGreeting] = useState("");
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    completed: 0,
  });
  const [showInstallModal, setShowInstallModal] = useState(false);

  /* ---------------- LOAD DASHBOARD SUMMARY ---------------- */
  const loadDashboardData = async () => {
  try {
    setLoading(true);
    const res = await apiClient.get("/agent-dashboard-summary");
    setStatusCounts(res.data.statusCounts);
  } catch (err) {
    console.error("Failed to load agent summary", err);
  } finally {
    setLoading(false);
  }
};


  /* ---------------- GREETING LOGIC ---------------- */
  useEffect(() => {
    const today = new Date().toDateString();
    const lastVisit = localStorage.getItem("agentDashboardLastVisit");

    if (lastVisit !== today) {
      setGreeting(getGreeting());
      localStorage.setItem("agentDashboardLastVisit", today);
    } else {
      setGreeting("Welcome back");
    }
  }, []);

  /* ---------------- REALTIME SOCKET UPDATES ---------------- */
useEffect(() => {
  console.log("🟢 AgentDashboard mounted");

  loadDashboardData();

  socket.on("connect", () => {
    console.log("⚡ Socket connected in AgentDashboard", socket.id);

    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user && user._id) {
      console.log("📌 Joining room:", user._id);
      socket.emit("join", { userId: user._id, role: user.role });
    }
  });

  socket.on("order_created", (order) => {
    console.log("🔥🔥🔥 order_created received in Dashboard:", order);
    loadDashboardData();
  });

  socket.on("order_status_updated", (order) => {
    console.log("🔥🔥🔥 order_status_updated received:", order);
    loadDashboardData();
  });

  socket.on("order_accepted", (order) => {
    console.log("🔥🔥🔥 order_accepted received:", order);
    loadDashboardData();
  });

  socket.on("order_deleted", () => {
    console.log("🔥🔥🔥 order_deleted received");
    loadDashboardData();
  });

  socket.on("order_collected", () => {
    console.log("🔥🔥🔥 order_collected received");
    loadDashboardData();
  });

  return () => {
    socket.off("order_created");
    socket.off("order_status_updated");
    socket.off("order_accepted");
    socket.off("order_deleted");
    socket.off("order_collected");
    socket.off("connect");
  };
}, []);



  /* ---------------- TABS ---------------- */
 const tabs = [
  {
    key: "pending",
    label: "Pending",
    count: statusCounts.pending,
    icon: <Clock className="w-6 h-6 text-orange-700" />,
    gradient: "bg-gradient-to-r from-orange-200 via-amber-200 to-yellow-100",
    border: "border-orange-300",
    path: "/agent/deliveries/pending",
  },
  {
    key: "processing",
    label: "Processing",
    count: statusCounts.processing,
    icon: <PackageCheck className="w-6 h-6 text-indigo-700" />,
    gradient: "bg-gradient-to-r from-indigo-200 via-blue-200 to-sky-50",
    border: "border-indigo-500",
    path: "/agent/deliveries/processing",
  },
  {
    key: "shipped",
    label: "Shipped",
    count: statusCounts.shipped,
    icon: <Truck className="w-6 h-6 text-blue-700" />,
    gradient: "bg-gradient-to-r from-blue-200 via-sky-200 to-cyan-100",
    border: "border-blue-500",
    path: "/agent/deliveries/shipped",
  },
  {
    key: "delivered",
    label: "Delivered",
    count: statusCounts.delivered,
    icon: <PackageCheck className="w-6 h-6 text-purple-700" />,
    gradient: "bg-gradient-to-r from-fuchsia-200 via-purple-200 to-violet-100",
    border: "border-purple-500",
    path: "/agent/deliveries/delivered",
  },
  {
    key: "completed",
    label: "Completed",
    count: statusCounts.completed,
    icon: <CheckCircle className="w-6 h-6 text-green-700" />,
    gradient: "bg-gradient-to-r from-emerald-200 via-green-200 to-lime-100",
    border: "border-green-500",
    path: "/agent/deliveries/completed",
  },
];


  return (
    <div className="pt-0 ">
      {/* WELCOME STRIP */}
    <PageHeader
  title={`${userName} - Partner Dashboard`}
  role="agent"
 
/>

      <div className="space-y-4 p-3">
        {/* CARDS */}
      {loading ? (
  <Loader text="Loading dashboard..." />
) : (
  <div className="grid grid-cols-1 gap-4">
    {tabs.map((tab) => (
      <div
        key={tab.key}
        onClick={() => navigate(tab.path)}
        className={`
          relative
          p-4
          rounded-2xl
          cursor-pointer
          transition-all duration-300
          shadow-md 
          hover:-translate-y-0.25
          border-l-4
          ${tab.border}
          ${tab.gradient}
        `}
      >
        <div className="flex items-center gap-4">
          <div className="bg-white/60 backdrop-blur-sm p-3 rounded-xl shadow-sm">
            {tab.icon}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 tracking-wide">
              {tab.label}
            </p>
            <p className="text-2xl font-bold text-gray-900">
              {tab.count}
              <span className="text-base font-semibold text-gray-600 ml-1">
                Orders
              </span>
            </p>
          </div>
        </div>
      </div>
    ))}
  </div>
)}


      <div className="flex justify-center mt-6">
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md shadow-sm w-full">
    <h3 className="font-semibold text-blue-900 text-sm mb-3 text-center">
      Install App
    </h3>

    {/* Android */}
    <div className="mb-4">
      <div className="font-semibold text-sm mb-1">For Android (Chrome):</div>
      <ol className="list-decimal ml-4 text-sm text-gray-700 space-y-1">
        <li>Tap the <b>three dots</b> menu.</li>
        <li>Select <b>"Add to Home screen"</b> or <b>"Install app"</b>.</li>
        <li>Follow the prompts.</li>
      </ol>
    </div>

    {/* iPhone */}
    {/* <div>
      <div className="font-semibold text-sm mb-1">For iPhone (Safari):</div>
      <ol className="list-decimal ml-4 text-sm text-gray-700 space-y-1">
        <li>Tap the <b>Share</b> icon.</li>
        <li>Select <b>"Add to Home Screen"</b>.</li>
        <li>Tap <b>Add</b>.</li>
      </ol>
    </div> */}
  </div>
</div>
      </div>
    </div>
  );
};

export default AgentDashboard;
